import React, { ChangeEvent, useCallback, useLayoutEffect, useMemo, useState } from "react";
import { DateObject } from "../../../../domain/fundamental/DateObject";
import { CareRecipientName, CareRecipientNameMaxLength } from "../../../../domain/care_recipient/CareRecipientName";
import { CareRecipientHouseId, CareRecipientHouseIdMaxLength } from "../../../../domain/care_recipient/CareRecipientHouseId";
import { ServiceProviderId } from "../../../../domain/service_provider/ServiceProviderId";
import { DateTime } from "../../../../domain/fundamental/DateTime";
import { InputTextBox } from "../../common/input/InputTextBox";
import {
    ServiceProviderSelector,
    ServiceProviderSelectorProps
} from "../service_provider/select/ServiceProviderSelector";
import { AdminDatePicker } from "../AdminDatePicker";
import { AdminDateTimePicker } from "../AdminDateTimePicker";
import { Button } from "../../common/button/Button";
import { Sensor, homeApplianceList, thermoHygrometerList, motionSensorList } from "../../../../domain/care_recipient/Sensor";
import { useInput } from "../../../hooks/common/useInput";
import { InputSelectBox } from "../../common/input/InputSelectBox";
import { CareRecipientClassification, getCareRecipientClassificationFromValue } from "../../../../domain/care_recipient/CareRecipientClassification";
import { LabelValueSet } from "../../../../domain/utility/LabelValueSet";
import { InputCheckBox } from "../../common/input/InputCheckBox";
import { DateSelector } from "./components/DateSelector";
import { useBoolean } from "../../../hooks/common/useBoolean";
import { BranchCode } from "../../../../domain/care_recipient/BranchCode";
import { CustomerCode } from "../../../../domain/care_recipient/CustomerCode";

export const CareRecipientEditComponent = React.memo(function CareRecipientEditComponent(props: CareRecipientEditComponentProps) {
    const {
        type,
        classificationSelectList,
        whenUpdateClicked
    } = props;

    useLayoutEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const [name, , handleNameChanged] = useInput(props.name?.value || "");
    const [birthday, , handleBirthdayChanged] = useInput(props.birthday);
    const [houseId, , handleHouseIdChanged] = useInput(props.houseId?.value || "");
    const [branchCode, , handleBranchCodeChanged] = useInput(props.branchCode?.value || "");
    const [customerCode, , handleCustomerCodeChanged] = useInput(props.customerCode?.value || "");
    const [serviceProviderId, setServiceProviderId] = useState(props.serviceProviderId);
    const [dataSyncStartTime, , handleStartTimeChanged] = useInput(props.dataSyncStartTime);
    const [dataSyncEndTime, , handleEndTimeChanged] = useInput(props.dataSyncEndTime);
    const [sensors, setSensors] = useState(props.sensors);
    const [classification, setClassification] = useState(props.classification);
    const [alertPeriodStart, setAlertPeriodStart] = useState(props.alertPeriod?.start ?? null);
    const [alertPeriodEnd, setAlertPeriodEnd] = useState(props.alertPeriod?.end ?? null);
    const [alertPause, , , toggleAlertPause] = useBoolean(!!props.alertPause);

    const [nameErrorMessage, setNameErrorMessage] = useState("");
    const [houseIdErrorMessage, setHouseIdErrorMessage] = useState("");
    const [birthdayErrorMessage, setBirthdayErrorMessage] = useState("");
    const [serviceProviderErrorMessage, setServiceProviderErrorMessage] = useState("");
    const [dataSyncStartTimeError, setDataSyncStartTimeError] = useState("");

    const serviceProviderIdProps: ServiceProviderSelectorProps = useMemo(() => ({
        currentServiceProviderId: serviceProviderId,
        isNullable: true,
        whenServiceProviderChanged: (serviceProviderId: ServiceProviderId | null) => setServiceProviderId(serviceProviderId),
        hasError: !!serviceProviderErrorMessage.length
    }), [serviceProviderId, serviceProviderErrorMessage]);

    const handleSensorChanged = useCallback((e: ChangeEvent<HTMLInputElement>) =>
        setSensors(pervList => pervList.includes(e.target.value) ? pervList.filter(sensor => sensor !== e.target.value) : [...pervList, e.target.value])
    , []);

    const setCalendarDate = useCallback((startDate: DateObject, endDate: DateObject) => {
        setAlertPeriodStart(startDate);
        setAlertPeriodEnd(endDate);
    }, []);

    const resetErrors = useCallback(() => {
        setNameErrorMessage("");
        setHouseIdErrorMessage("");
        setBirthdayErrorMessage("");
        setServiceProviderErrorMessage("");
    }, []);

    const handleUpdateClicked = useCallback(() => {
        let hasError = false;
        resetErrors();
        
        // 名前の入力チェック
        if (!name) {
            hasError = true;
            setNameErrorMessage("名前を入力してください。");
        }       
        // 名前の長さチェック（最大200文字）
        if (!CareRecipientName.fromString(name).isValidLength()) {
            hasError = true;
            setNameErrorMessage(`名前は${CareRecipientNameMaxLength}文字以内で入力してください。`);
        }

        // ハウスIDの入力チェック
        if (!houseId) {
            hasError = true;
            setHouseIdErrorMessage("ハウスIDを入力して下さい。");
        } 
        // ハウスIDの長さチェック（最大100文字）
        if (!CareRecipientHouseId.fromString(houseId).isValidLength()) {
            hasError = true;
            setHouseIdErrorMessage(`ハウスIDは${CareRecipientHouseIdMaxLength}文字以内で入力してください。`);
        }

        // 生年月日の入力チェック
        if (!birthday) {
            hasError = true;
            setBirthdayErrorMessage("生年月日を入力してください。");
        } 

        // サービスプロバイダの入力チェック
        if (!serviceProviderId) {
            hasError = true;
            setServiceProviderErrorMessage("サービスプロバイダを選択してください。");
        }

        // 更新時のみデータ連携開始日時の入力チェック
        if (type === "更新" && !dataSyncStartTime) {
            hasError = true;
            setDataSyncStartTimeError("データ連携開始日時を入力してください。");
        }

        if (hasError) {
            return;
        }

        whenUpdateClicked(
            CareRecipientName.fromString(name),
            birthday!!,
            CareRecipientHouseId.fromString(houseId),
            serviceProviderId!!,
            dataSyncStartTime,
            dataSyncEndTime,
            sensors,
            customerCode,
            branchCode,
            classification,
            (alertPeriodStart && alertPeriodEnd) ? { start: alertPeriodStart, end: alertPeriodEnd } : null,
            alertPause,
        );
    }, [type, resetErrors, name, houseId, birthday, serviceProviderId, dataSyncStartTime, dataSyncEndTime, sensors, classification, alertPeriodStart, alertPeriodEnd, alertPause, whenUpdateClicked, branchCode, customerCode]);

    const isMatchedSensor = useCallback((sensor: Sensor) => sensors.includes(sensor.value), [sensors]);
    
    // 高齢者分類のリストがある場合のみ高齢者分類を設定可能とする
    const shouldDisplayClassificationSelect = useMemo(() => !!classificationSelectList.length, [classificationSelectList]);

    const displayClassificationSelectList = useMemo(() => [new LabelValueSet("選択してください", ""), ...classificationSelectList], [classificationSelectList]);

    const handleSelectBoxChanged = useCallback((value: string) => setClassification(getCareRecipientClassificationFromValue(value) ?? null), []);

    return (
        <div className={"admin_care_recipient_edit_component"}>
            <div className={"input_area"}>
                <div className={"input_area__each name"}>
                    <div className={"label"}>名前 (必須)</div>
                    <InputTextBox value={name} handleTextChanged={handleNameChanged} hasError={!!nameErrorMessage.length} />
                    <div className="error_message">{nameErrorMessage}</div>
                </div>
                <div className={"input_area__each house_id"}>
                    <div className={"label"}>ハウスID (必須)</div>
                    <InputTextBox value={houseId} handleTextChanged={handleHouseIdChanged} hasError={!!houseIdErrorMessage.length} />
                    <div className="error_message">{houseIdErrorMessage}</div>
                </div>
                <div className={"input_area__each house_id"}>
                    <div className={"label"}>支店コード</div>
                    <InputTextBox value={branchCode} handleTextChanged={handleBranchCodeChanged} hasError={!!houseIdErrorMessage.length} />
                    <div className="error_message">{houseIdErrorMessage}</div>
                </div>
                <div className={"input_area__each house_id"}>
                    <div className={"label"}>顧客コード</div>
                    <InputTextBox value={customerCode} handleTextChanged={handleCustomerCodeChanged} hasError={!!houseIdErrorMessage.length} />
                    <div className="error_message">{houseIdErrorMessage}</div>
                </div>
                <div className={"input_area__each birthday"}>
                    <div className={"label"}>生年月日 (必須)</div>
                    <AdminDatePicker currentDate={birthday} whenDateChanged={handleBirthdayChanged} />
                    <div className="error_message">{birthdayErrorMessage}</div>
                </div>
                <div className={"input_area__each service_provider_id"}>
                    <div className={"label"}>サービスプロバイダ (必須)</div>
                    <ServiceProviderSelector {...serviceProviderIdProps} />
                    <div className="error_message">{serviceProviderErrorMessage}</div>
                </div>
                <div className={"input_area__each sensor"}>
                    <div className={"title"}>センサー・家電</div>
                    <div className="body">
                        <div className="content">
                            <div className="label">家電 (複数選択可)</div>
                            <div className="sensor">
                                {homeApplianceList.map((sensor) => (
                                    <div className="check_box_row" key={sensor.value}>
                                        <label className="check_box">
                                            <input type="checkbox" value={sensor.value} checked={isMatchedSensor(sensor)} onChange={handleSensorChanged} />
                                            <span className="icon_checked"/>
                                            {sensor.label}
                                        </label>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="content">
                            <div className="label">温湿度計 (複数選択可)</div>
                            <div className="sensor">
                                {thermoHygrometerList.map((sensor) => (
                                    <div className="check_box_row" key={sensor.value}>
                                        <label className="check_box">
                                            <input type="checkbox" value={sensor.value} checked={isMatchedSensor(sensor)} onChange={handleSensorChanged} />
                                            <span className="icon_checked" />
                                            {sensor.label}
                                        </label>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="content">
                            <div className="label">人感センサー (複数選択可)</div>
                            <div className="sensor">
                                {motionSensorList.map((sensor) => (
                                    <div className="check_box_row" key={sensor.value}>
                                        <label className="check_box">
                                            <input type="checkbox" value={sensor.value} checked={isMatchedSensor(sensor)} onChange={handleSensorChanged} />
                                            <span className="icon_checked" />
                                            {sensor.label}
                                        </label>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
                <div className={"input_area__each start_time"}>
                    <div className={"label"}>データ連携開始日時</div>
                    <AdminDateTimePicker currentDateTime={dataSyncStartTime} whenDateTimeChanged={handleStartTimeChanged} isNullable />
                    <div className="error_message">{dataSyncStartTimeError}</div>
                </div>
                <div className={"input_area__each end_time"}>
                    <div className={"label"}>データ連携終了日時</div>
                    <AdminDateTimePicker currentDateTime={dataSyncEndTime} whenDateTimeChanged={handleEndTimeChanged} isNullable />
                </div>
                {shouldDisplayClassificationSelect && (
                    <div className={"input_area__each classification"}>
                        <div className={"label"}>高齢者分類</div>
                        <InputSelectBox value={classification?.value ?? ""} list={displayClassificationSelectList} handleSelectBoxChanged={handleSelectBoxChanged} />
                    </div>
                )}
                <div className="input_area__each alert_period">
                    <div className="label">平均値期間設定</div>
                    <div className="date_selector">
                        <DateSelector
                            defaultStartDate={alertPeriodStart}
                            defaultEndDate={alertPeriodEnd}
                            handleSaveClicked={setCalendarDate}
                            dataSyncStartDate={dataSyncStartTime?.date}
                        />
                    </div>
                </div>
                <div className="input_area__each">
                    <InputCheckBox label={"アラート一時停止"} checked={alertPause} handleCheckBoxChanged={toggleAlertPause} />
                </div>
            </div>
            <div className={"button_area"}>
                <Button label={type} onClick={handleUpdateClicked} />
            </div>
        </div>
    );
});

export type CareRecipientEditComponentProps = {
    type: "登録" | "更新"
    name: CareRecipientName | null,
    birthday: DateObject | null,
    houseId: CareRecipientHouseId | null,
    branchCode?: BranchCode| null,
    customerCode?: CustomerCode| null,
    serviceProviderId: ServiceProviderId | null,
    dataSyncStartTime: DateTime | null,
    dataSyncEndTime: DateTime | null,
    sensors: string[],
    classification: CareRecipientClassification | null,
    classificationSelectList: CareRecipientClassification[],
    alertPeriod: {
        start: DateObject,
        end: DateObject
    } | null,
    alertPause: boolean | null,
    whenUpdateClicked: (
        name: CareRecipientName,
        birthday: DateObject,
        houseId: CareRecipientHouseId,
        serviceProviderId: ServiceProviderId,
        dataSyncStartTime: DateTime | null,
        dataSyncEndTime: DateTime | null,
        sensors: (string)[],
        customerCode: string,
        branchCode: string,
        classification: CareRecipientClassification | null,
        alertPeriod: {
            start: DateObject,
            end: DateObject
        } | null,
        alertPause: boolean | null,
        ) => void,
};
